import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DataTableFilterSectionProps {
  label: string;
  active: boolean;
  onClear: () => void;
  children: ReactNode;
}

export function DataTableFilterSection({
  label,
  active,
  onClear,
  children,
}: DataTableFilterSectionProps) {
  const [open, setOpen] = useState(active);

  useEffect(() => {
    if (!active) setOpen(false);
  }, [active]);

  return (
    <div className="border-b border-border/40 last:border-b-0">
      <div className="flex items-center justify-between py-3">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <span
            className={cn(
              'text-xs font-semibold uppercase tracking-wide',
              active ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {label}
          </span>
          {active && (
            <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold">
              1
            </span>
          )}
          <ChevronDown
            size={14}
            className={cn(
              'ml-auto text-muted-foreground transition-transform duration-150',
              open && 'rotate-180',
            )}
          />
        </button>
        {active && (
          <button
            type="button"
            onClick={onClear}
            className="ml-3 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            title={`Clear ${label} filter`}
            aria-label={`Clear ${label} filter`}
          >
            <X size={12} />
          </button>
        )}
      </div>

      <div
        inert={!open}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="min-w-0 pb-4">{children}</div>
        </div>
      </div>
    </div>
  );
}
